"use client";

import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PushNudgeCopy } from "../lib/push-nudge-copy";
import {
  PushNudgeMedallion,
  PushNudgeMockRow,
  PushNudgeReassurance,
} from "./push-nudge-parts";

export type PushNudgeCardProps = {
  copy: PushNudgeCopy;
  isBusy?: boolean;
  onPrimary: () => void;
  onDismiss: () => void;
  className?: string;
};

/**
 * V2 — the inline card.
 *
 * Lives in the screen's own scroll content, with no scrim and no focus trap,
 * for the utility moments: a waitlist position, a request awaiting someone
 * else's decision. The user is mid-task and reading; interrupting them with a
 * modal here would cost more than the permission is worth.
 *
 * It renders at its resting state immediately rather than waiting on an
 * intersection observer, so a card below the fold is still fully readable the
 * moment it is scrolled to.
 */
export function PushNudgeCard({
  copy,
  isBusy = false,
  onPrimary,
  onDismiss,
  className,
}: PushNudgeCardProps) {
  return (
    <section
      aria-label={copy.headline}
      className={cn(
        "rounded-[24px] border border-border bg-surface p-4 shadow-sm",
        "dark:border-glass-border-10 dark:bg-glass-bg-05 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <PushNudgeMedallion size="sm" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <h3 className="text-[16px] leading-tight font-semibold text-balance text-text-primary">
            {copy.headline}
          </h3>
          {copy.subhead ? (
            <p className="text-[13px] leading-snug text-text-secondary">
              {copy.subhead}
            </p>
          ) : null}
        </div>
      </div>

      {copy.mockRows.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2">
          {copy.mockRows.map((row, index) => (
            <PushNudgeMockRow
              key={`${row.title}-${row.body}`}
              row={row}
              index={index}
            />
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <Button
          onClick={onPrimary}
          disabled={isBusy}
          className="h-11 flex-1 rounded-full bg-brand text-[15px] font-semibold text-brand-foreground transition-transform hover:bg-brand-hover active:scale-[0.99] disabled:opacity-60 dark:bg-brand dark:text-brand-foreground"
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            copy.primaryLabel
          )}
        </Button>
        <Button
          variant="ghost"
          onClick={onDismiss}
          disabled={isBusy}
          className="h-11 rounded-full px-4 text-[15px] font-medium text-text-secondary hover:bg-glass-bg-04 dark:text-text-secondary dark:hover:bg-glass-bg-06"
        >
          {copy.dismissLabel}
        </Button>
      </div>

      <div className="mt-2.5">
        <PushNudgeReassurance>{copy.reassurance}</PushNudgeReassurance>
      </div>
    </section>
  );
}
